import { FunctionComponent, useId } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'clsx';
import { NavIcon } from '../../Navbar/NavIcon';
import { NavbarItem } from '../../Navbar';
import { routeWarmProps } from '@/perf/routePreload';
import { RailTooltip } from './RailTooltip';
import { ProStar } from '@/components/ProStar';

type RailNavItemProps = {
  item: NavbarItem;
  collapsed?: boolean;
  onRequestExpand?: () => void;
  /** True when the rail is force-collapsed by route — expand is a no-op there. */
  forced?: boolean;
  /** Controlled open state — coordinated by Rail so only one dropdown stays open. */
  isOpen?: boolean;
  /** Called with the label to open, or null to collapse. */
  onOpenChange?: (key: string | null) => void;
};
const linkClasses =
  'block w-full rounded-lg focus-visible:outline-2 focus-visible:outline-frost-brand cursor-pointer';

const activationBadge = (
  <span className="ml-auto shrink-0 rounded-frost-sm bg-frost-ink/5 px-1.5 py-0.5 text-fs-9 font-semibold text-frost-ink-soft">
    Activar
  </span>
);

export const RailNavItem: FunctionComponent<RailNavItemProps> = ({
  item,
  collapsed = false,
  onRequestExpand,
  forced = false,
  isOpen = false,
  onOpenChange
}) => {
  const {
    label,
    icon,
    isSelected = false,
    disabled = false,
    isDropdown,
    dropdownContent,
    link,
    onClick,
    external,
    danger = false,
    badge,
    count = 0,
    locked = false,
    addOn = false
  } = item;

  const navigate = useNavigate();
  const submenuId = useId();

  const firstLinkedChild = dropdownContent?.find((c) => !!c.link && !c.disabled && !c.link.includes('http'));

  // Solo el activo se marca: lavado azul plano y el ícono en baldosa azul. El resto,
  // ícono suelto sin baldosa. Filas de 36px (38 en el riel contraído, baldosa de 36).
  const baseClasses = classNames(
    // Interlineado 1.35 como la maqueta; el paso de 36px lo da el min-h + my-px.
    'group relative my-px flex items-center rounded-lg text-fs-13-5 leading-[1.35] outline-hidden',
    'transition-[background-color,color,padding] duration-200 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]',
    'focus-visible:ring-2 focus-visible:ring-frost-brand/30 focus-visible:ring-offset-1',
    collapsed ? 'min-h-[38px] justify-center gap-0 p-px' : 'min-h-[34px] gap-[11px] px-1.5 py-1',
    isSelected && !danger ? 'font-semibold' : 'font-medium',
    {
      'bg-frost-brand-wash text-frost-ink': isSelected && !danger && !collapsed,
      'text-frost-ink': isSelected && !danger && collapsed,
      'text-frost-coral hover:bg-frost-coral-wash': danger && !disabled,
      'text-frost-ink-soft hover:bg-[rgba(var(--f-ink-rgb),0.05)] hover:text-frost-ink':
        !isSelected && !disabled && !danger && !locked,
      'text-frost-ink-soft hover:bg-[rgba(var(--f-ink-rgb),0.05)]': locked && !isSelected,
      'cursor-not-allowed opacity-50': disabled
    }
  );

  // Ícono de 12px en la barra expandida y 14px en la contraída (como la maqueta), en
  // tinta suave; solo el activo va en blanco sobre su baldosa azul.
  const iconClasses = classNames(
    'shrink-0 transition-colors duration-200',
    collapsed ? 'text-fs-14' : 'text-fs-12',
    danger
      ? 'text-frost-coral opacity-90'
      : isSelected
        ? 'text-white'
        : locked
          ? 'text-frost-ink-soft'
          : 'text-frost-ink-soft group-hover:text-frost-ink'
  );

  const iconNode = icon ? (
    <span
      className={classNames(
        'relative grid shrink-0 place-items-center rounded-lg transition-colors duration-200',
        collapsed ? 'h-9 w-9' : 'h-[26px] w-[26px]',
        isSelected && !danger ? 'bg-frost-brand' : 'bg-transparent'
      )}
    >
      <NavIcon name={icon} className={iconClasses} />
      {/* Estrellita de Pro, igual que en los sub-ítems; el texto para lectores va con la etiqueta. */}
      {addOn && (
        <ProStar
          size="xs"
          ring
          label={null}
          className={classNames('absolute', collapsed ? 'right-1.5 top-1.5' : 'right-0 top-0.5')}
        />
      )}
      {collapsed && count > 0 && (
        <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-frost-brand shadow-[0_0_0_2px_var(--f-paper)]" />
      )}
    </span>
  ) : null;

  // Con el rail colapsado (o en el botón de una sección) el nombre accesible sale del aria-label,
  // que pisa el texto de adentro: lo de Pro tiene que decirlo ahí también.
  const a11yLabel = addOn ? `${label} (incluido en Pro)` : label;

  const badgeNode =
    count > 0 ? (
      <span className="ml-auto grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-frost-brand px-1.5 text-fs-11 font-semibold tabular-nums text-white">
        {count}
      </span>
    ) : (
      badge && (
        <span className="shrink-0 rounded-frost-sm bg-frost-brand-wash px-1.5 py-0.5 text-fs-9 font-bold leading-[1.35] text-frost-brand-deep shadow-[inset_0_0_0_1px_rgba(var(--f-brand-rgb),0.15)]">
          {badge}
        </span>
      )
    );

  if (!isDropdown) {
    const content = (
      <div className={baseClasses}>
        {iconNode}
        {!collapsed && (
          <span className="min-w-0">
            {label}
            {addOn && <span className="sr-only"> (incluido en Pro)</span>}
          </span>
        )}
        {!collapsed && locked && activationBadge}
        {!collapsed && badgeNode}
      </div>
    );

    if (locked) {
      return (
        <RailTooltip label={label} enabled={collapsed}>
          <button
            type="button"
            onClick={onClick}
            className={`${linkClasses} text-left`}
            aria-label={collapsed ? a11yLabel : undefined}
          >
            {content}
          </button>
        </RailTooltip>
      );
    }

    if (external) {
      return (
        <RailTooltip label={label} enabled={collapsed}>
          <a
            href={link}
            onClick={onClick}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClasses}
            aria-label={collapsed ? a11yLabel : undefined}
          >
            {content}
          </a>
        </RailTooltip>
      );
    }

    return (
      <RailTooltip label={label} enabled={collapsed}>
        <Link
          to={link}
          aria-current={isSelected ? 'page' : undefined}
          onClick={onClick}
          {...routeWarmProps(link)}
          className={linkClasses}
          aria-label={collapsed ? a11yLabel : undefined}
        >
          {content}
        </Link>
      </RailTooltip>
    );
  }

  const handleDropdownClick = () => {
    onClick?.();
    if (collapsed) {
      if (!forced) {
        // Rail colapsado por el usuario: lo desplegamos y abrimos esta sección
        // para mostrar su submenú (no hay lugar para los sub-items en modo slim).
        onRequestExpand?.();
        onOpenChange?.(label);
      } else if (firstLinkedChild?.link) {
        // Rail forzado a slim por la ruta (editores): no se puede desplegar, así
        // que navegamos al primer sub-item enlazado como atajo.
        firstLinkedChild.onClick?.();
        navigate(firstLinkedChild.link);
        onOpenChange?.(label);
      } else {
        onOpenChange?.(label);
      }
      return;
    }
    onOpenChange?.(isOpen ? null : label);
  };
  const parentContent = (
    <>
      {iconNode}
      {!collapsed && (
        <span>
          {label}
          {addOn && <span className="sr-only"> (incluido en Pro)</span>}
        </span>
      )}
      {!collapsed && badgeNode}
    </>
  );

  return (
    <RailTooltip label={label} enabled={collapsed}>
      <div className="flex items-center">
        {link && !collapsed ? (
          <Link
            to={link}
            onClick={onClick}
            {...routeWarmProps(link)}
            aria-current={isSelected ? 'page' : undefined}
            className={classNames(baseClasses, 'min-w-0 flex-1')}
          >
            {parentContent}
          </Link>
        ) : (
          <button
            type="button"
            aria-label={a11yLabel}
            onClick={handleDropdownClick}
            aria-expanded={!collapsed && isOpen}
            aria-controls={submenuId}
            disabled={disabled}
            className={classNames(baseClasses, 'min-w-0 flex-1 text-left')}
          >
            {parentContent}
          </button>
        )}
        {!collapsed && (
          <button
            type="button"
            aria-label={`${isOpen ? 'Cerrar' : 'Abrir'} ${label}`}
            aria-expanded={isOpen}
            aria-controls={submenuId}
            onClick={handleDropdownClick}
            className="grid min-h-9 w-9 shrink-0 place-items-center rounded-lg text-frost-ink-soft hover:bg-frost-brand-wash focus-visible:ring-2 focus-visible:ring-frost-brand"
          >
            <NavIcon
              name="chevron-down"
              className={classNames('text-fs-10 transition-transform duration-200', isOpen && 'rotate-180')}
            />
          </button>
        )}
      </div>

      {!collapsed && isOpen && dropdownContent && dropdownContent.length > 0 && (
        <div
          id={submenuId}
          className="mt-1 ml-[22px] flex flex-col gap-0.5 border-l border-frost-hair-stronger pl-3"
        >
          {dropdownContent.map((sub) => {
            const subClasses = classNames(
              'flex min-h-9 w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fs-12-5 font-medium',
              'transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-frost-brand',
              {
                'bg-frost-brand-wash font-semibold text-frost-ink':
                  sub.isSelected && !sub.disabled && !sub.locked,
                'text-frost-ink-mid hover:bg-frost-paper hover:text-frost-ink':
                  !sub.isSelected && !sub.disabled && !sub.locked,
                // Bloqueada: existe, se ve, pero todavía no aplica a este negocio.
                'text-frost-ink-soft hover:bg-frost-paper': sub.locked && !sub.disabled,
                'opacity-50 cursor-not-allowed pointer-events-none': sub.disabled
              }
            );
            const subContent = (
              <>
                {sub.icon && (
                  <span className="relative flex shrink-0 items-center">
                    <NavIcon
                      name={sub.icon}
                      className={classNames(
                        'w-3.5 shrink-0 transition-colors duration-150',
                        sub.isSelected ? 'text-frost-brand' : 'text-frost-ink-mid opacity-85'
                      )}
                    />
                    {/* Estrellita de Pro: distingue lo que viene con Pro de lo que ya
                      viene con el plan, sin repetir un chip en cada sección. */}
                    {sub.addOn && (
                      <ProStar size="xs" ring label={null} className="absolute -right-1 -top-1" />
                    )}
                  </span>
                )}
                <span className="min-w-0">
                  {sub.label}
                  {sub.addOn && <span className="sr-only"> (incluido en Pro)</span>}
                </span>
                {sub.badge && (
                  <span
                    className={classNames(
                      'shrink-0 whitespace-nowrap rounded-frost-sm px-1.5 py-0.5 text-fs-9 font-bold leading-[1.35]',
                      sub.badge === 'Activar'
                        ? 'bg-frost-ink/[0.05] text-frost-ink-soft'
                        : 'bg-frost-brand-wash text-frost-brand-deep shadow-[inset_0_0_0_1px_rgba(var(--f-brand-rgb),0.15)]'
                    )}
                  >
                    {sub.badge}
                  </span>
                )}
                {typeof sub.length === 'number' && sub.length > 0 && (
                  <span
                    className={classNames(
                      'ml-auto text-fs-10-5',
                      sub.isSelected ? 'text-frost-brand' : 'text-frost-ink-whisper'
                    )}
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {sub.length}
                  </span>
                )}
                {sub.locked && activationBadge}
              </>
            );

            // Bloqueada no navega: abre el modal que explica cómo habilitarla.
            return sub.locked ? (
              <button key={sub.label} type="button" onClick={sub.onClick} className={subClasses}>
                {subContent}
              </button>
            ) : (
              <Link
                key={sub.label}
                to={sub.link}
                aria-current={sub.isSelected ? 'page' : undefined}
                onClick={sub.onClick}
                {...routeWarmProps(sub.link)}
                className={subClasses}
              >
                {subContent}
              </Link>
            );
          })}
        </div>
      )}
    </RailTooltip>
  );
};
